import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  ChangeEvent,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import Layout from '@/components/Layout/Layout';
import BackButton from '@/components/ui/BackButton';
import CardLayout from '@/components/Layout/CardLayout';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';

import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import {
  showToastNotification,
  getFilterUrl,
  orderByList,
} from '@/utility/helper';
import { RootState } from '@/redux/store';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';
import { ITableRowData } from '@/interfaces/common.model';
import { RoutesLink } from '@/utility/RouteLinks';

interface IAssignedUser {
  id: string;
  name: string;
  profilePhoto?: string | null;
}

interface IAssetRequest {
  id?: string;
  description?: string | null;
  receivedAt?: string | null;
  assignedUser?: IAssignedUser;
  asset?: {
    id: string;
    name: string;
    status: number;
  };
  assetCategory?: {
    id: string;
    name: string;
  };
}

interface IHistoryEntry {
  id: string;
  text: string;
  createdAt: string;
  updatedAt?: string;
  deletedAt?: string | null;
  userId?: string | null;
  assetId?: string;
  assetRequestId?: string;
  asset?: {
    id: string;
    name: string;
    serialNo?: string;
    status: number;
    assetCategory?: { name: string };
    assetRequest?: IAssetRequest;
  };
  assetRequest?: IAssetRequest;
}

interface IHistoryResponse {
  status: boolean;
  data: {
    'Assest History': IHistoryEntry[];
    pageInfo: {
      total: number;
      currentPage: number;
      totalPages: number;
    };
  };
}

interface IHistoryTableData extends ITableRowData {
  id: string;
  index: string;
  name: string;
  category: string;
  event: string;
  date: string;
  empNameWithImage: string;
  // assetStatus: number;
  userId?: string;
}

const AssetHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const tableView = useSelector((state: RootState) => state?.user?.tableView);

  const [tableLoader, setTableLoader] = useState(false);
  const [historyData, setHistoryData] = useState<IHistoryTableData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleViewEmployee = (data: IHistoryTableData) => {
    if (data.userId) {
      navigate(`${RoutesLink.detailEmployee}/${data.userId}`);
    }
  };

  const tableHeading = useMemo(() => {
    const columns: ITableColumn<IHistoryTableData>[] = [
      { value: 'index', label: '#' },
      { value: 'name', label: 'Asset' },
      { value: 'category', label: 'Category' },
      { value: 'event', label: 'Event' },
      { value: 'date', label: 'Date' },
      { value: 'empNameWithImage', label: 'Assigned To' },
      // { value: 'assetStatus', label: 'Status' },
    ];

    if (tableView === 'grid') {
      return columns.filter(
        (col) => !['event', 'date', 'category'].includes(col.value as string),
      );
    }
    return columns;
  }, [tableView]);

  const fetchHistory = useCallback(async () => {
    if (!id) return;
    try {
      setTableLoader(true);
      const filter = {
        pageNumber: filterDetails?.currentPage || 1,
        pageSize: filterDetails?.pageSize || 10,
        search: searchQuery,
        orderBy: filterDetails?.orderBy,
        assetId: id,
      };

      Object.keys(filter).forEach(
        (key) =>
          filter[key as keyof typeof filter] === undefined &&
          delete filter[key as keyof typeof filter],
      );

      const API =
        API_URL + API_ENDPOINT.historyAssets + '?' + getFilterUrl(filter);
      const response: IHistoryResponse = await callApi(API, { method: 'GET' });

      if (response.status) {
        const rawEntries = response.data['Assest History'] || [];

        const mapped: IHistoryTableData[] = rawEntries.map((entry, idx) => {
          const assetReq = entry.assetRequest;
          // const asset = assetReq?.asset || entry.asset || {};
          const categoryName =
            assetReq?.assetCategory?.name ||
            (entry.asset?.assetCategory?.name ?? 'N/A');
          const assignedUser =
            assetReq?.assignedUser || entry.asset?.assetRequest?.assignedUser;
          const assignedName = assignedUser?.name || '—';
          const profilePhoto = assignedUser?.profilePhoto || 'null';
          const userId = assignedUser?.id || '';

          return {
            id: entry.id,
            index: String(
              ((filterDetails?.currentPage || 1) - 1) *
                (filterDetails?.pageSize || 10) +
                idx +
                1,
            ).padStart(2, '0'),
            name: assetReq?.asset?.name || '—',
            category: categoryName,
            event: entry.text || '—',
            date: moment(entry.createdAt).format('DD-MMM-YYYY HH:mm'),
            empNameWithImage: `${assignedName}##${profilePhoto}`,
            // assetStatus: assetReq?.asset?.status ?? -1,
            userId: userId,
          };
        });

        setHistoryData(mapped);
        dispatch(
          setUpdateFilterStates({
            count: response.data.pageInfo.total,
            totalPages: response.data.pageInfo.totalPages,
          }),
        );
      } else {
        showToastNotification('danger', 'Failed to fetch history');
      }
    } catch (error) {
      const err = error as IApiErrorResponse;
      showToastNotification('danger', err.message || 'Something went wrong');
    } finally {
      setTableLoader(false);
    }
  }, [
    id,
    filterDetails?.currentPage,
    filterDetails?.pageSize,
    filterDetails?.orderBy,
    searchQuery,
    dispatch,
  ]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    dispatch(setUpdateFilterStates({ currentPage: 1 }));
  };

  const orderByHandler = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      setUpdateFilterStates({ orderBy: e.target.value, currentPage: 1 }),
    );
  };

  const pageChangeHandler = (pageNo: number) => {
    if (pageNo > 0 && pageNo <= (filterDetails?.totalPages || 1)) {
      dispatch(setUpdateFilterStates({ currentPage: pageNo }));
    }
  };

  const handleChangePageSize = (value: number) => {
    dispatch(setUpdateFilterStates({ currentPage: 1, pageSize: value }));
  };

  // ---------- Render ----------
  return (
    <Layout>
      <div className="content container-fluid">
        <BackButton label="Asset History" isDetail={true} />

        <CardLayout cardStyle="mb-4">
          <FilterComponent
            filterArray={{ isShow: false }}
            handleSearch={handleSearchChange}
            orderByHandler={orderByHandler}
            orderByList={orderByList}
            // sortHandler={sortHandler}
            isGrid={false}
          />

          <CommonTable<IHistoryTableData>
            tableHeading={tableHeading}
            tableData={historyData}
            count={filterDetails?.count || 0}
            pageSize={filterDetails?.pageSize || 10}
            isLoading={tableLoader}
            currentPage={filterDetails?.currentPage || 1}
            totalPages={filterDetails?.totalPages || 1}
            pageChangeHandler={pageChangeHandler}
            handleChangePageSize={handleChangePageSize}
            handleViewDetail={handleViewEmployee}
          />
        </CardLayout>
      </div>
    </Layout>
  );
};

export default AssetHistory;
